import { useState } from "react";
import { useBuyPanelsStore } from "../store";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Button } from "@/components/ui/Button";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/Card";

const FREQUENCIES = [
  { value: "Daily", label: "Daily" },
  { value: "Weekly", label: "Weekly" },
  { value: "Every 2 weeks", label: "Every 2 weeks" },
  { value: "Every 3 weeks", label: "Every 3 weeks" },
  { value: "Every 4 weeks", label: "Every 4 weeks" },
  { value: "Monthly", label: "Monthly" },
  { value: "Every 2 months", label: "Every 2 months" },
  { value: "Every 12 weeks", label: "Every 12 weeks" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Every 4 months", label: "Every 4 months" },
  { value: "Half yearly", label: "Half yearly" },
  { value: "Yearly", label: "Yearly" },
];

export default function RecurringPaymentOptions() {
  const frequency = useBuyPanelsStore((state) => state.frequency);
  const setFrequency = useBuyPanelsStore((state) => state.setFrequency);
  const endCondition = useBuyPanelsStore((state) => state.endCondition);
  const setEndCondition = useBuyPanelsStore((state) => state.setEndCondition);
  const endDate = useBuyPanelsStore((state) => state.endDate);
  const setEndDate = useBuyPanelsStore((state) => state.setEndDate);
  const paymentCount = useBuyPanelsStore((state) => state.paymentCount);
  const setPaymentCount = useBuyPanelsStore((state) => state.setPaymentCount);

  const [date, setDate] = useState<Date | undefined>(endDate ? new Date(endDate) : undefined);

  const handleDateSelect = (d: Date | undefined) => {
    setDate(d);
    if (d) {
      setEndDate(format(d, "yyyy-MM-dd"));
    } else {
      setEndDate("");
    }
  };

  return (
    <Card className="bg-brand-neutral-900 border-brand-neutral-700 mb-4 py-0 animate-in fade-in slide-in-from-top-2">
      <CardContent className="p-4 space-y-4">
        <h3 className="font-medium text-white mb-2">Recurring Payment Options</h3>
        
        <div className="space-y-2">
          <Label className="text-white">Make this payment</Label>
          <Select value={frequency} onValueChange={setFrequency}>
            <SelectTrigger className="w-full bg-brand-neutral-800 border-zinc-700 text-white">
              <SelectValue placeholder="- Select one -" />
            </SelectTrigger>
            <SelectContent className="bg-brand-neutral-800 border-zinc-700 text-white">
              {FREQUENCIES.map((freq) => (
                <SelectItem key={freq.value} value={freq.value}>
                  {freq.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-white">End payments</Label>
          <Select value={endCondition} onValueChange={(val: any) => setEndCondition(val)}>
            <SelectTrigger className="w-full bg-brand-neutral-800 border-zinc-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-brand-neutral-800 border-zinc-700 text-white">
              <SelectItem value="no-end-date">No end date</SelectItem>
              <SelectItem value="certain-date">On a certain date</SelectItem>
              <SelectItem value="number-of-payments">After a number of payments</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {endCondition === "certain-date" && (
          <div className="space-y-2">
            <Label className="text-white">End payments on</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={"outline"}
                  className={cn(
                    "w-full justify-start text-left font-normal bg-brand-neutral-800 border-zinc-700 text-white hover:bg-brand-neutral-700 hover:text-white",
                    !date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-brand-neutral-800 border-zinc-700" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={handleDateSelect}
                  initialFocus
                  className="text-white"
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-gray-400">Enter the date after which you want no further recurring payments to be made.</p>
          </div>
        )}

        {endCondition === "number-of-payments" && (
          <div className="space-y-2">
            <Label className="text-white">Number of payments</Label>
            <Input
              type="number"
              min="1"
              value={paymentCount}
              onChange={(e) => setPaymentCount(e.target.value)}
              className="w-full bg-brand-neutral-800 border-zinc-700 text-white"
              placeholder="e.g. 12"
            />
          </div>
        )}

        <div className="pt-2">
          <p className="text-xs text-gray-400 italic">
            If payments fall on a non-processing day, funds will be sent on the next Banking Day.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}